import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Plus, Search, Edit, Trash2, Building2, Users, Settings, Key, Mail } from 'lucide-react';
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { facilitiesApi, queryKeys, Facility, CreateFacilityRequest, UpdateFacilityRequest } from '@/lib/api';

export default function FacilitiesPage() {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [editingFacility, setEditingFacility] = useState<Facility | null>(null);
  const [managingAccountsFacility, setManagingAccountsFacility] = useState<Facility | null>(null);
  const [showCreateAccountForm, setShowCreateAccountForm] = useState(false);
  const [editingAccount, setEditingAccount] = useState<any>(null);
  const queryClient = useQueryClient();

  // Fetch facilities with search
  const { data: facilitiesData, isLoading, error } = useQuery({
    queryKey: queryKeys.facilities.list({ search: searchTerm }),
    queryFn: () => {
      const params: { limit: number; search?: string } = { limit: 50 };
      if (searchTerm) {
        params.search = searchTerm;
      }
      return facilitiesApi.list(params);
    },
  });

  // Create facility mutation
  const createMutation = useMutation({
    mutationFn: facilitiesApi.create,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.facilities.all });
      setShowCreateForm(false);
    },
  });

  // Update facility mutation
  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: UpdateFacilityRequest }) =>
      facilitiesApi.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.facilities.all });
      setEditingFacility(null);
    },
  });

  // Delete facility mutation
  const deleteMutation = useMutation({
    mutationFn: facilitiesApi.delete,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.facilities.all });
    },
  });

  // Facility accounts queries and mutations
  const { data: accountsData, isLoading: accountsLoading } = useQuery({
    queryKey: ['facility-accounts', managingAccountsFacility?.facilityId],
    queryFn: () => facilitiesApi.accounts.list(managingAccountsFacility!.facilityId),
    enabled: !!managingAccountsFacility,
  });

  const createAccountMutation = useMutation({
    mutationFn: ({ facilityId, data }: { facilityId: string; data: { email: string; password: string } }) =>
      facilitiesApi.accounts.create(facilityId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['facility-accounts', managingAccountsFacility?.facilityId] });
      setShowCreateAccountForm(false);
    },
  });

  const updateAccountMutation = useMutation({
    mutationFn: ({ facilityId, accountId, data }: { facilityId: string; accountId: number; data: { email?: string; password?: string } }) =>
      facilitiesApi.accounts.update(facilityId, accountId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['facility-accounts', managingAccountsFacility?.facilityId] });
      setEditingAccount(null);
    },
  });

  const deleteAccountMutation = useMutation({
    mutationFn: ({ facilityId, accountId }: { facilityId: string; accountId: number }) =>
      facilitiesApi.accounts.delete(facilityId, accountId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['facility-accounts', managingAccountsFacility?.facilityId] });
    },
  });

  const facilities = (facilitiesData?.facilities || []).sort((a, b) => a.name.localeCompare(b.name));
  const totalFacilities = facilitiesData?.pagination?.total || 0;
  const activeFacilities = facilities.filter(f => f.isActive).length;

  const handleCreateSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const formData = new FormData(event.currentTarget);
    
    const notes = formData.get('notes') as string;
    const facilityData: CreateFacilityRequest = {
      facilityId: formData.get('facilityId') as string,
      name: formData.get('name') as string,
      isActive: formData.get('isActive') === 'on',
      maxLogos: parseInt(formData.get('maxLogos') as string) || 20,
      requiresTransparency: formData.get('requiresTransparency') === 'on',
      ...(notes && { notes }),
    };

    createMutation.mutate(facilityData);
  };

  const handleUpdateSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!editingFacility) return;

    const formData = new FormData(event.currentTarget);
    
    const notes = formData.get('notes') as string;
    const updateData: UpdateFacilityRequest = {
      name: formData.get('name') as string,
      isActive: formData.get('isActive') === 'on',
      maxLogos: parseInt(formData.get('maxLogos') as string) || 20,
      requiresTransparency: formData.get('requiresTransparency') === 'on',
      ...(notes && { notes }),
    };

    updateMutation.mutate({ id: editingFacility.id, data: updateData });
  };

  const handleDelete = (facility: Facility) => {
    if (window.confirm(`Are you sure you want to delete "${facility.name}"? This action cannot be undone.`)) {
      deleteMutation.mutate(facility.id);
    }
  };

  return (
    <div className="container mx-auto px-4 py-8">
      {/* Header */}
      <div className="mb-8">
        <div className="flex justify-between items-center mb-4">
          <div>
            <h1 className="text-3xl font-bold flex items-center gap-2">
              <Building2 className="h-8 w-8" />
              Facilities Management
            </h1>
            <p className="text-muted-foreground">Manage curling facilities and their settings</p>
          </div>
          <Button onClick={() => setShowCreateForm(true)} className="flex items-center gap-2">
            <Plus className="h-4 w-4" />
            Add Facility
          </Button>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-2">
                <Building2 className="h-5 w-5 text-blue-600" />
                <div>
                  <p className="text-2xl font-bold">{totalFacilities}</p>
                  <p className="text-sm text-muted-foreground">Total Facilities</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-2">
                <Users className="h-5 w-5 text-green-600" />
                <div>
                  <p className="text-2xl font-bold">{activeFacilities}</p>
                  <p className="text-sm text-muted-foreground">Active Facilities</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-2">
                <Settings className="h-5 w-5 text-orange-600" />
                <div>
                  <p className="text-2xl font-bold">{totalFacilities - activeFacilities}</p>
                  <p className="text-sm text-muted-foreground">Inactive Facilities</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Search */}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <input
            type="text"
            placeholder="Search facilities..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-input rounded-md bg-background"
          />
        </div>
      </div>

      {/* Create Form Modal */}
      {showCreateForm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <Card className="w-full max-w-md mx-4">
            <CardHeader>
              <CardTitle>Create New Facility</CardTitle>
              <CardDescription>Add a new curling facility to the system</CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleCreateSubmit} className="space-y-4">
                <div>
                  <label htmlFor="facilityId" className="block text-sm font-medium mb-1">
                    Facility ID *
                  </label>
                  <input
                    id="facilityId"
                    name="facilityId"
                    type="text"
                    required
                    pattern="[a-zA-Z0-9_-]+"
                    title="Only letters, numbers, underscores, and hyphens allowed"
                    className="w-full px-3 py-2 border border-input rounded-md bg-background"
                    placeholder="e.g., TORONTO-CC"
                  />
                </div>
                <div>
                  <label htmlFor="name" className="block text-sm font-medium mb-1">
                    Facility Name *
                  </label>
                  <input
                    id="name"
                    name="name"
                    type="text"
                    required
                    className="w-full px-3 py-2 border border-input rounded-md bg-background"
                    placeholder="e.g., Toronto Curling Club"
                  />
                </div>
                <div>
                  <label htmlFor="maxLogos" className="block text-sm font-medium mb-1">
                    Max Logos
                  </label>
                  <input
                    id="maxLogos"
                    name="maxLogos"
                    type="number"
                    min="1"
                    max="100"
                    defaultValue="20"
                    className="w-full px-3 py-2 border border-input rounded-md bg-background"
                  />
                </div>
                <div className="flex items-center space-x-2">
                  <input
                    id="isActive"
                    name="isActive"
                    type="checkbox"
                    defaultChecked
                    className="rounded border-input"
                  />
                  <label htmlFor="isActive" className="text-sm font-medium">
                    Active
                  </label>
                </div>
                <div className="flex items-center space-x-2">
                  <input
                    id="requiresTransparency"
                    name="requiresTransparency"
                    type="checkbox"
                    defaultChecked
                    className="rounded border-input"
                  />
                  <label htmlFor="requiresTransparency" className="text-sm font-medium">
                    Requires Transparency
                  </label>
                </div>
                <div>
                  <label htmlFor="notes" className="block text-sm font-medium mb-1">
                    Notes
                  </label>
                  <textarea
                    id="notes"
                    name="notes"
                    rows={3}
                    className="w-full px-3 py-2 border border-input rounded-md bg-background"
                    placeholder="Optional notes about this facility..."
                  />
                </div>
                <div className="flex justify-end space-x-2">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setShowCreateForm(false)}
                  >
                    Cancel
                  </Button>
                  <Button type="submit" disabled={createMutation.isPending}>
                    {createMutation.isPending ? 'Creating...' : 'Create Facility'}
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Edit Form Modal */}
      {editingFacility && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <Card className="w-full max-w-md mx-4">
            <CardHeader>
              <CardTitle>Edit Facility</CardTitle>
              <CardDescription>Update facility information</CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleUpdateSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Facility ID</label>
                  <input
                    type="text"
                    value={editingFacility.facilityId}
                    disabled
                    className="w-full px-3 py-2 border border-input rounded-md bg-muted text-muted-foreground"
                  />
                  <p className="text-xs text-muted-foreground mt-1">Facility ID cannot be changed</p>
                </div>
                <div>
                  <label htmlFor="edit-name" className="block text-sm font-medium mb-1">
                    Facility Name *
                  </label>
                  <input
                    id="edit-name"
                    name="name"
                    type="text"
                    required
                    defaultValue={editingFacility.name}
                    className="w-full px-3 py-2 border border-input rounded-md bg-background"
                  />
                </div>
                <div>
                  <label htmlFor="edit-maxLogos" className="block text-sm font-medium mb-1">
                    Max Logos
                  </label>
                  <input
                    id="edit-maxLogos"
                    name="maxLogos"
                    type="number"
                    min="1"
                    max="100"
                    defaultValue={editingFacility.maxLogos}
                    className="w-full px-3 py-2 border border-input rounded-md bg-background"
                  />
                </div>
                <div className="flex items-center space-x-2">
                  <input
                    id="edit-isActive"
                    name="isActive"
                    type="checkbox"
                    defaultChecked={editingFacility.isActive}
                    className="rounded border-input"
                  />
                  <label htmlFor="edit-isActive" className="text-sm font-medium">
                    Active
                  </label>
                </div>
                <div className="flex items-center space-x-2">
                  <input
                    id="edit-requiresTransparency"
                    name="requiresTransparency"
                    type="checkbox"
                    defaultChecked={editingFacility.requiresTransparency}
                    className="rounded border-input"
                  />
                  <label htmlFor="edit-requiresTransparency" className="text-sm font-medium">
                    Requires Transparency
                  </label>
                </div>
                <div>
                  <label htmlFor="edit-notes" className="block text-sm font-medium mb-1">
                    Notes
                  </label>
                  <textarea
                    id="edit-notes"
                    name="notes"
                    rows={3}
                    defaultValue={editingFacility.notes || ''}
                    className="w-full px-3 py-2 border border-input rounded-md bg-background"
                    placeholder="Optional notes about this facility..."
                  />
                </div>
                <div className="flex justify-end space-x-2">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setEditingFacility(null)}
                  >
                    Cancel
                  </Button>
                  <Button type="submit" disabled={updateMutation.isPending}>
                    {updateMutation.isPending ? 'Updating...' : 'Update Facility'}
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Account Management Modal */}
      {managingAccountsFacility && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <Card className="w-full max-w-4xl mx-4 max-h-[90vh] overflow-hidden">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Key className="h-5 w-5" />
                Manage Accounts - {managingAccountsFacility.name}
              </CardTitle>
              <CardDescription>
                Manage login accounts for facility: {managingAccountsFacility.facilityId}
              </CardDescription>
            </CardHeader>
            <CardContent className="overflow-y-auto">
              <div className="space-y-4">
                {/* Add Account Button */}
                <div className="flex justify-between items-center">
                  <h3 className="text-lg font-semibold">Login Accounts</h3>
                  <Button
                    onClick={() => setShowCreateAccountForm(true)}
                    className="flex items-center gap-2"
                    size="sm"
                  >
                    <Plus className="h-4 w-4" />
                    Add Account
                  </Button>
                </div>

                {/* Accounts List */}
                {accountsLoading ? (
                  <div className="text-center py-8">
                    <p className="text-muted-foreground">Loading accounts...</p>
                  </div>
                ) : accountsData?.accounts.length === 0 ? (
                  <div className="text-center py-8">
                    <Mail className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                    <p className="text-muted-foreground">
                      No accounts found. Create the first account to get started.
                    </p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {[...(accountsData?.accounts ?? [])].sort((a, b) => a.email.localeCompare(b.email)).map((account) => (
                      <Card key={account.id}>
                        <CardContent className="p-4">
                          <div className="flex justify-between items-center">
                            <div>
                              <div className="flex items-center gap-2 mb-1">
                                <Mail className="h-4 w-4 text-blue-600" />
                                <span className="font-medium">{account.email}</span>
                              </div>
                              <div className="text-sm text-muted-foreground">
                                Created: {new Date(account.createdAt).toLocaleDateString()}
                                {account.updatedAt !== account.createdAt && (
                                  <span className="ml-4">
                                    Updated: {new Date(account.updatedAt).toLocaleDateString()}
                                  </span>
                                )}
                              </div>
                            </div>
                            <div className="flex items-center gap-2">
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => setEditingAccount(account)}
                                className="flex items-center gap-1"
                              >
                                <Edit className="h-3 w-3" />
                                Edit
                              </Button>
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => {
                                  if (window.confirm(`Are you sure you want to delete the account "${account.email}"?`)) {
                                    deleteAccountMutation.mutate({
                                      facilityId: managingAccountsFacility.facilityId,
                                      accountId: account.id,
                                    });
                                  }
                                }}
                                disabled={deleteAccountMutation.isPending}
                                className="flex items-center gap-1 text-red-600 hover:text-red-700"
                              >
                                <Trash2 className="h-3 w-3" />
                                Delete
                              </Button>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                )}

                {/* Close Button */}
                <div className="flex justify-end pt-4 border-t">
                  <Button
                    variant="outline"
                    onClick={() => setManagingAccountsFacility(null)}
                  >
                    Close
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Create Account Form Modal */}
      {showCreateAccountForm && managingAccountsFacility && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[60]">
          <Card className="w-full max-w-md mx-4">
            <CardHeader>
              <CardTitle>Create Account</CardTitle>
              <CardDescription>
                Add a new login account for {managingAccountsFacility.name}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  const formData = new FormData(e.currentTarget);
                  createAccountMutation.mutate({
                    facilityId: managingAccountsFacility.facilityId,
                    data: {
                      email: formData.get('email') as string,
                      password: formData.get('password') as string,
                    },
                  });
                }}
                className="space-y-4"
              >
                <div>
                  <label htmlFor="account-email" className="block text-sm font-medium mb-1">
                    Email Address *
                  </label>
                  <input
                    id="account-email"
                    name="email"
                    type="email"
                    required
                    className="w-full px-3 py-2 border border-input rounded-md bg-background"
                    placeholder="user@example.com"
                  />
                </div>
                <div>
                  <label htmlFor="account-password" className="block text-sm font-medium mb-1">
                    Password *
                  </label>
                  <input
                    id="account-password"
                    name="password"
                    type="password"
                    required
                    minLength={8}
                    className="w-full px-3 py-2 border border-input rounded-md bg-background"
                    placeholder="Minimum 8 characters"
                  />
                </div>
                <div className="flex justify-end space-x-2">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setShowCreateAccountForm(false)}
                  >
                    Cancel
                  </Button>
                  <Button type="submit" disabled={createAccountMutation.isPending}>
                    {createAccountMutation.isPending ? 'Creating...' : 'Create Account'}
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Edit Account Form Modal */}
      {editingAccount && managingAccountsFacility && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[60]">
          <Card className="w-full max-w-md mx-4">
            <CardHeader>
              <CardTitle>Edit Account</CardTitle>
              <CardDescription>
                Update account for {managingAccountsFacility.name}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  const formData = new FormData(e.currentTarget);
                  const email = formData.get('email') as string;
                  const password = formData.get('password') as string;
                  
                  const updateData: { email?: string; password?: string } = {};
                  if (email !== editingAccount.email) {
                    updateData.email = email;
                  }
                  if (password) {
                    updateData.password = password;
                  }

                  updateAccountMutation.mutate({
                    facilityId: managingAccountsFacility.facilityId,
                    accountId: editingAccount.id,
                    data: updateData,
                  });
                }}
                className="space-y-4"
              >
                <div>
                  <label htmlFor="edit-account-email" className="block text-sm font-medium mb-1">
                    Email Address *
                  </label>
                  <input
                    id="edit-account-email"
                    name="email"
                    type="email"
                    required
                    defaultValue={editingAccount.email}
                    className="w-full px-3 py-2 border border-input rounded-md bg-background"
                  />
                </div>
                <div>
                  <label htmlFor="edit-account-password" className="block text-sm font-medium mb-1">
                    New Password
                  </label>
                  <input
                    id="edit-account-password"
                    name="password"
                    type="password"
                    minLength={8}
                    className="w-full px-3 py-2 border border-input rounded-md bg-background"
                    placeholder="Leave blank to keep current password"
                  />
                  <p className="text-xs text-muted-foreground mt-1">
                    Only enter a password if you want to change it
                  </p>
                </div>
                <div className="flex justify-end space-x-2">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setEditingAccount(null)}
                  >
                    Cancel
                  </Button>
                  <Button type="submit" disabled={updateAccountMutation.isPending}>
                    {updateAccountMutation.isPending ? 'Updating...' : 'Update Account'}
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Facilities List */}
      <div className="space-y-4">
        {isLoading ? (
          <div className="text-center py-8">
            <p className="text-muted-foreground">Loading facilities...</p>
          </div>
        ) : error ? (
          <div className="text-center py-8">
            <p className="text-red-600">Error loading facilities: {error.message}</p>
          </div>
        ) : facilities.length === 0 ? (
          <div className="text-center py-8">
            <Building2 className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
            <p className="text-muted-foreground">
              {searchTerm ? 'No facilities found matching your search.' : 'No facilities found. Create your first facility to get started.'}
            </p>
          </div>
        ) : (
          facilities.map((facility) => (
            <Card key={facility.id}>
              <CardContent className="p-6">
                <div className="flex justify-between items-start">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <h3 className="text-lg font-semibold">{facility.name}</h3>
                      <Badge variant={facility.isActive ? 'default' : 'secondary'}>
                        {facility.isActive ? 'Active' : 'Inactive'}
                      </Badge>
                      {facility.requiresTransparency && (
                        <Badge variant="outline">Transparency Required</Badge>
                      )}
                    </div>
                    <p className="text-sm text-muted-foreground mb-2">
                      ID: <code className="bg-muted px-1 rounded">{facility.facilityId}</code>
                    </p>
                    <div className="flex items-center gap-4 text-sm text-muted-foreground mb-2">
                      <span>Max Logos: {facility.maxLogos}</span>
                      <span>Created: {new Date(facility.createdAt).toLocaleDateString()}</span>
                      <span>Updated: {new Date(facility.updatedAt).toLocaleDateString()}</span>
                    </div>
                    {facility.notes && (
                      <p className="text-sm text-muted-foreground bg-muted p-2 rounded mt-2">
                        {facility.notes}
                      </p>
                    )}
                  </div>
                  <div className="flex items-center gap-2 ml-4">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setManagingAccountsFacility(facility)}
                      className="flex items-center gap-1"
                    >
                      <Key className="h-3 w-3" />
                      Manage Accounts
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => navigate(`/facilities/${facility.id}/settings`)}
                      className="flex items-center gap-1"
                    >
                      <Settings className="h-3 w-3" />
                      Settings
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleDelete(facility)}
                      disabled={deleteMutation.isPending}
                      className="flex items-center gap-1 text-red-600 hover:text-red-700"
                    >
                      <Trash2 className="h-3 w-3" />
                      Delete
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))
        )}
      </div>

      {/* Error Messages */}
      {createMutation.error && (
        <div className="fixed bottom-4 right-4 bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">
          Error creating facility: {createMutation.error.message}
        </div>
      )}
      {updateMutation.error && (
        <div className="fixed bottom-4 right-4 bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">
          Error updating facility: {updateMutation.error.message}
        </div>
      )}
      {deleteMutation.error && (
        <div className="fixed bottom-4 right-4 bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">
          Error deleting facility: {deleteMutation.error.message}
        </div>
      )}
    </div>
  );
}
